import { tgHtmlToPlain, type PermissionOverrides, type Permissions } from "@kickwin/shared";
import { escapeHtml } from "./tg-format";
import { pruneTranslations, withKeys } from "./i18n";
import { routeForAuthError } from "./auth-routing";
import type { ApiResponse } from "@kickwin/shared";

/**
 * Admin API client.
 *
 * Every response uses the spec §12 envelope, so success and failure are
 * distinguished by the body rather than by guessing from the status code.
 * Better Auth supplies an HttpOnly session cookie the browser sends by
 * itself; there is no token to manage here, which is why
 * `credentials: "include"` is the only auth setup (decision 48).
 */

const BASE = import.meta.env.VITE_API_BASE ?? "";

/** Where the panel sends someone to sign in. */
export const LOGIN_PATH = "/login";

export class ApiError extends Error {
  constructor(
    readonly code: string,
    message: string,
    readonly status: number,
  ) {
    super(message);
    this.name = "ApiError";
  }
}

async function request<T>(path: string, init: RequestInit = {}): Promise<T> {
  const response = await fetch(`${BASE}/api${path}`, {
    ...init,
    credentials: "include",
    headers: {
      ...(init.body ? { "content-type": "application/json" } : {}),
      ...(init.headers ?? {}),
    },
  });

  let body: ApiResponse<T>;
  try {
    body = (await response.json()) as ApiResponse<T>;
  } catch {
    // HTML where JSON belongs almost always means this panel is being served
    // from a hostname the Admin API is not routed on — the static site
    // answers /api/* with index.html — so say that rather than "could not
    // read", which sends people looking for a server fault that is not there.
    const servedHtml = response.ok && (response.headers.get("content-type") ?? "").includes("html");
    throw new ApiError(
      "unreadable_response",
      response.status === 503
        ? "Sign-in is not configured for this environment yet."
        : servedHtml
          ? "This address does not serve the Admin API. Open the panel at its own domain."
          : "The server sent something this page could not read.",
      response.status,
    );
  }

  if (!body.success) {
    // Being signed out, and being signed in without having finished setup,
    // are different destinations — and sending the second to the login page
    // would loop (see auth-routing). Replace rather than push, so Back does
    // not land on a page that is about to bounce again.
    const target = routeForAuthError(body.error.code);
    if (target && window.location.pathname !== target) {
      window.location.replace(target);
    }
    throw new ApiError(body.error.code, body.error.message, response.status);
  }

  return body.data;
}

export const api = {
  get: <T>(path: string) => request<T>(path),
  post: <T>(path: string, data?: unknown) =>
    request<T>(path, {
      method: "POST",
      ...(data === undefined ? {} : { body: JSON.stringify(data) }),
    }),
  patch: <T>(path: string, data: unknown) =>
    request<T>(path, { method: "PATCH", body: JSON.stringify(data) }),
  put: <T>(path: string, data: unknown) =>
    request<T>(path, { method: "PUT", body: JSON.stringify(data) }),
  del: <T>(path: string) => request<T>(path, { method: "DELETE" }),
};

// --- shapes the panel reads ------------------------------------------------

export type Role = "super_admin" | "admin" | "viewer";

/** Alerts derived from events can be dismissed; live conditions cannot. */
export const dismissAlert = (botId: string | number, type: string) =>
  api.post(`/bots/${botId}/alerts/dismiss`, { type });

export interface Me {
  id: number;
  email: string;
  role: Role;
  roleName: string | null;
  /** What this person may do and see (decision 42). The server checks again. */
  permissions: Permissions;
}

/** A role template (decision 42). */
export interface RoleTemplate {
  id: number;
  name: string;
  description: string | null;
  permissions: Permissions;
  isBuiltin: boolean;
  members: number;
}

export interface Bot {
  id: number;
  telegramBotId: number;
  username: string;
  title: string | null;
  status: "active" | "disabled";
  createdAt: string;
}

export interface AdminUser {
  id: number;
  email: string;
  role: Role;
  roleId: number | null;
  roleName: string | null;
  overrides: PermissionOverrides | null;
  isActive: boolean;
  lastSeenAt: string | null;
  botIds: number[];
}

export interface CsGroup {
  id: number;
  botId: number;
  telegramChatId: number;
  title: string | null;
  isForum: boolean;
  status: "discovered" | "active" | "left";
  perms: Record<string, boolean> | null;
  lastCheckedAt: string | null;
}

export interface Source {
  id: number;
  name: string;
  code: string;
  type: string;
  isActive: boolean;
  deepLink: string | null;
  firstTouchPlayers: number;
  latestTouchPlayers: number;
}

/** Creating a source also credits any earlier visits that used its code. */
export interface CreatedSource extends Source {
  relinkedEvents: number;
}

export interface UnknownCode {
  rawCode: string;
  uses: number;
  lastSeenAt: number;
}

export interface Player {
  id: number;
  playerNo: number;
  /** A number in full, "51***" when masked, null when hidden (decision 42). */
  telegramUserId: number | string | null;
  username: string | null;
  firstName: string | null;
  lastName: string | null;
  status: "active" | "blocked" | "deactivated" | "banned";
  topicActive: boolean;
  firstSourceId: number | null;
  latestSourceId: number | null;
  firstSeenAt: string;
  lastSeenAt: string | null;
}

export interface Page<T> {
  rows: T[];
  total: number;
  page: number;
  pageSize: number;
  redacted?: boolean;
}

export interface PlayerDetail {
  player: Player & { languageCode: string | null; isImported: boolean };
  topic: {
    messageThreadId: number;
    topicName: string;
    status: string;
    previousThreadIds: number[] | null;
  } | null;
  sourceHistory: {
    id: number;
    sourceId: number | null;
    sourceName: string | null;
    rawCode: string | null;
    eventType: string;
    createdAt: string;
  }[];
}

export interface MessageRow {
  id: number;
  direction: "player_to_cs" | "cs_to_player" | "bot_to_player" | "system";
  type: string;
  status: string;
  isRevoked: boolean;
  senderKind: string | null;
  createdAt: string;
  sentAt: string | null;
  text?: string | null;
  caption?: string | null;
  lastError?: string | null;
}

export interface Dashboard {
  players: { total: number; newToday: number; activeToday: number };
  messages: { today: number; failed: number };
  sources: {
    active: number;
    top: { sourceId: number | null; name: string | null; players: number }[];
  };
  alerts: {
    undeliveredPlayerMessages: number;
    groupPermissionProblems: number;
    rateLimitDelays: number;
    hasActiveGroup: boolean;
    groupPerms: Record<string, boolean> | null;
  };
}

export interface SystemEvent {
  id: number;
  severity: "debug" | "info" | "warn" | "error";
  type: string;
  telegramUserId: number | null;
  updateId: number | null;
  detail: unknown;
  createdAt: string;
}

export interface AuditLog {
  id: number;
  actor: string;
  actorRole: string | null;
  action: string;
  entity: string | null;
  entityId: string | null;
  beforeJson: unknown;
  afterJson: unknown;
  createdAt: string;
}

// --- automation, broadcasts, import (spec §8, §10, §11) ---------------------

export type MatchType = "exact" | "contains" | "first_contact" | "start" | "source" | "fallback";

export interface AutoReplyRule {
  id: number;
  name: string;
  priority: number;
  matchType: MatchType;
  matchValue: string | null;
  sourceId: number | null;
  responseText: string | null;
  replyMarkup: unknown;
  isActive: boolean;
}

export interface AutoReplyTest {
  matched: boolean;
  ruleId?: number;
  ruleName?: string;
  response?: string | null;
}

export interface BotCommand {
  id: number;
  command: string;
  description: string;
  responseText: string | null;
  menuId: number | null;
  replyMarkup: unknown;
  isActive: boolean;
}

export type BroadcastStatus = "draft" | "scheduled" | "running" | "paused" | "done" | "cancelled";
export type TargetKind = "all" | "first_source" | "latest_source" | "player";

export interface Broadcast {
  id: number;
  title: string;
  text: string | null;
  replyMarkup: unknown;
  targetKind: TargetKind;
  targetId: number | null;
  status: BroadcastStatus;
  scheduledFor: string | null;
  recipients: number;
  sent: number;
  failed: number;
  blocked: number;
  createdBy: string | null;
  /** Who pressed Send or scheduled it; null for a draft, or before 2026-10-08. */
  sentBy: string | null;
  createdAt: string;
  startedAt: string | null;
  finishedAt: string | null;
  successRate: number | null;
}

export interface BroadcastDetail {
  broadcast: Broadcast;
  successRate: number | null;
  failures: { playerId: number; status: string; error: string | null }[];
}

export interface ImportPreview {
  headers: string[];
  totalRows: number;
  sample: string[][];
  suggested: Record<string, string>;
  fields: { field: string; label: string; required: boolean }[];
}

export interface ImportChunk {
  jobId: number;
  processed: number;
  total: number;
  created: number;
  updated: number;
  skipped: number;
  errors: { row: number; reason: string }[];
  done: boolean;
  nextOffset: number | null;
}

export interface ImportJob {
  id: number;
  filename: string | null;
  status: "preview" | "running" | "done" | "failed" | "cancelled";
  totalRows: number;
  processedRows: number;
  createdPlayers: number;
  updatedPlayers: number;
  skippedRows: number;
  errors: { row: number; reason: string }[] | null;
  actor: string | null;
  createdAt: string;
  finishedAt: string | null;
}

/** Where Telegram is really delivering a bot's updates (asked of Telegram). */
export type WebhookStatus =
  | { available: false; expectedUrl: string }
  | {
      available: true;
      url: string;
      expectedUrl: string;
      matches: boolean;
      pendingUpdates: number;
      lastErrorAt: string | null;
      lastErrorMessage: string | null;
    };

// --- button trees (decision 35) ----------------------------------------------

export type MessageMedia = { kind: "photo"; url: string } | { kind: "video"; post: string };
export type ButtonStyle = "primary" | "success" | "danger";

export interface TreePage {
  text: string;
  media?: MessageMedia | undefined;
  buttons: TreeButton[][];
  backLabel?: string | undefined;
  /** Root page only: translations by language (decision 46). */
  i18n?: Record<string, Translation> | undefined;
}

/** `k` is a button's stable id; translations are keyed by it (decision 46). */
type TreeButtonBase = { text: string; style?: ButtonStyle | undefined; k?: string | undefined };

export type TreeButton =
  | (TreeButtonBase & { url: string })
  | (TreeButtonBase & { page: TreePage })
  /** Opens the player's Topic and tells CS which button they tapped (decision 36). */
  | (TreeButtonBase & { help: { reply?: string | undefined } });

/** One language's words for a tree; anything left out falls back to the default. */
export interface Translation {
  text?: string | undefined;
  /** A command's line in Telegram's command menu. */
  description?: string | undefined;
  nodes?:
    | Record<
        string,
        {
          label?: string | undefined;
          text?: string | undefined;
          back?: string | undefined;
          reply?: string | undefined;
        }
      >
    | undefined;
}

/** What a rule, command or broadcast stores; its text is kept separately. */
export interface TreeContent {
  v: 2;
  /** Texts in the tree are Telegram HTML (decision 45). */
  format?: "html" | undefined;
  media?: MessageMedia | undefined;
  buttons: TreeButton[][];
  i18n?: Record<string, Translation> | undefined;
}

export function isTreeContent(value: unknown): value is TreeContent {
  return !!value && typeof value === "object" && (value as { v?: unknown }).v === 2;
}

/** Every text in a tree saved before formatting, as Telegram HTML. */
function escapeTree(page: TreePage): TreePage {
  return {
    ...page,
    text: escapeHtml(page.text),
    buttons: page.buttons.map((row) =>
      row.map((b) =>
        "page" in b
          ? { ...b, page: escapeTree(b.page) }
          : "help" in b && b.help.reply
            ? { ...b, help: { reply: escapeHtml(b.help.reply) } }
            : b,
      ),
    ),
  };
}

/**
 * An owner's text and stored markup as one editable page. The editor always
 * works in Telegram HTML, so older plain text is escaped on the way in.
 */
export function pageFromOwner(text: string | null, markup: unknown): TreePage {
  const content = isTreeContent(markup) ? markup : null;
  const page = { text: text ?? "", media: content?.media, buttons: content?.buttons ?? [] };
  const html = content?.format === "html" ? page : escapeTree(page);
  return withKeys({ ...html, i18n: content?.i18n });
}

/** An owner's text as the player reads it, without formatting. */
export function plainOf(text: string, markup: unknown): string {
  return isTreeContent(markup) && markup.format === "html" ? tgHtmlToPlain(text) : text;
}

/** The markup to save for a page; always present, since it marks the format. */
export function markupFromPage(page: TreePage): TreeContent {
  const i18n = pruneTranslations(page.i18n);
  return {
    v: 2,
    format: "html",
    ...(page.media ? { media: page.media } : {}),
    buttons: page.buttons,
    ...(i18n ? { i18n } : {}),
  };
}

export function countButtons(page: TreePage): number {
  return page.buttons.flat().reduce((n, b) => n + 1 + ("page" in b ? countButtons(b.page) : 0), 0);
}
